import type React from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { ColorName } from 'styles/config/colors';

import ConditionalWrapper from '@polyai/common/utils/conditionalWrapper';

import { IconButton } from 'components/atoms/IconButton';
import { CloseIcon, SearchIcon } from 'components/atoms/Icons';
import { KeyboardSafeInput } from 'components/atoms/KeyboardSafeInputs';
import LoadingIcon from 'components/atoms/LoadingIcon';
import * as Styled from './InPlaceSearchField.styled';

export interface InPlaceSearchFieldProps
  extends Omit<React.HTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  open?: boolean;
  searchInput: string;
  setSearchInput: (searchText: string) => void;
  isLoading?: boolean;
  handleClose?: () => void;
  primaryColor?: ColorName;
  secondaryColor?: ColorName;
  hoverColor?: ColorName;
  fullWidth?: boolean;
  autoFocus?: boolean;
  shrinkIcon?: boolean;
}

const InPlaceSearchField: React.FC<InPlaceSearchFieldProps> = ({
  open,
  searchInput,
  setSearchInput,
  isLoading = false,
  handleClose,
  primaryColor = 'iconPrimary',
  secondaryColor = 'iconSecondary',
  hoverColor = 'iconPrimaryHover',
  fullWidth = false,
  autoFocus = true,
  shrinkIcon = true,
  ...props
}) => {
  const [isOpenState, setIsOpenState] = useState(searchInput ? true : false);
  const isOpen = open ?? isOpenState;

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing
  useEffect(() => {
    if (searchInput) {
      openSearch();
    }
  }, [searchInput]);

  const search = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const openSearch = () => {
    setIsOpenState(true);
  };

  const closeSearch = () => {
    setSearchInput('');
    setIsOpenState(false);
    if (handleClose) {
      handleClose();
    }
  };

  const htmlElRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isOpen && htmlElRef.current && autoFocus) {
      htmlElRef.current.focus();
    }
  }, [autoFocus, isOpen]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && htmlElRef.current) {
      htmlElRef.current.blur();
      closeSearch();
    }
  };

  return (
    <Styled.Wrapper $fullWidth={fullWidth}>
      <Styled.SearchFieldWrapper $fullWidth={fullWidth} $isOpen={isOpen}>
        <Styled.IconWrapper>
          {isLoading ? (
            <LoadingIcon size={14} />
          ) : (
            <ConditionalWrapper
              condition={!isOpen}
              wrapper={children => (
                <IconButton
                  aria-label="Search"
                  data-test-id="search-btn"
                  hoverTarget={open === undefined}
                  hoverTargetColor={hoverColor}
                  onClick={openSearch}
                >
                  {children}
                </IconButton>
              )}
            >
              <SearchIcon
                color={secondaryColor}
                size={isOpen && shrinkIcon ? 'sm' : 'md'}
              />
            </ConditionalWrapper>
          )}
        </Styled.IconWrapper>

        <Styled.InputWrapper $fullWidth={fullWidth} $isOpen={isOpen}>
          <KeyboardSafeInput
            ref={htmlElRef}
            autoFocus={autoFocus}
            data-test-id="search-input-field"
            placeholder="Search"
            placeholderColor={secondaryColor}
            style={{ width: '100%' }}
            tabIndex={!isOpen ? -1 : undefined}
            textColor={primaryColor}
            value={searchInput}
            onChange={search}
            onKeyDown={handleKeyDown}
            {...props}
          />
          {open === undefined && (
            <Styled.IconWrapper>
              <IconButton
                aria-label="Close search"
                data-test-id="close-search-btn"
                hoverSize="xl"
                hoverTargetColor={hoverColor}
                tabIndex={!isOpen ? -1 : undefined}
                hoverTarget
                onClick={closeSearch}
              >
                <CloseIcon color={primaryColor} size="sm" />
              </IconButton>
            </Styled.IconWrapper>
          )}
        </Styled.InputWrapper>
      </Styled.SearchFieldWrapper>
      <Styled.Underline
        $color={primaryColor}
        $fullWidth={fullWidth}
        $isOpen={isOpen}
      />
    </Styled.Wrapper>
  );
};

export default InPlaceSearchField;
